import { useCallback, useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {
  createIndividualAccountRequest,
  getIndividualAccountStatusRequest,
  submitIndividualAccountKycRequest,
} from "../Services/individualAccountApi";
import { buildBusinessDetailsPayload } from "../Components/Molecules/IndividualAccountOnboardingModal/utils";
import { birthDateToIso } from "../Utils/masks";

const LOCAL_STORAGE_KEY = "@BCBankline:IndividualAccount";

const readLocalIndividualAccount = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeLocalIndividualAccount = (payload) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
};

const normalizeStatusResponse = (data = {}) => {
  const status = data.status || (data.exists ? "active" : "none");
  return {
    exists: Boolean(data.exists || status === "active" || status === "pending_review"),
    status,
    verificationStatus: data.verification_status || data.verificationStatus || null,
    documentPending: data.document_pending === true || data.documentPending === true,
    pending: data.pending === true,
  };
};

const buildPfPayload = ({ personal, address, liveness }) => ({
  full_name: personal.fullName,
  document: personal.document.replace(/\D/g, ""),
  document_type: "cpf",
  birth_date: birthDateToIso(personal.birthDate) || personal.birthDate,
  email: personal.email,
  phone: personal.phone.replace(/\D/g, ""),
  is_company_account: false,
  liveness_session_id: liveness.sessionId,
  address: {
    zip_code: address.zipCode.replace(/\D/g, ""),
    street: address.street,
    number: address.number,
    complement: address.complement,
    neighborhood: address.neighborhood,
    city: address.city,
    state: address.state,
  },
});

const buildPjPayload = ({ company, beneficialOwners, liveness }) => ({
  business_details: buildBusinessDetailsPayload({ company, beneficialOwners }),
  liveness_session_id: liveness.sessionId,
});

export const useIndividualAccount = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [status, setStatus] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [documentPending, setDocumentPending] = useState(false);
  const [pending, setPending] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const applyNormalizedStatus = useCallback((normalized) => {
    setStatus(normalized.status);
    setVerificationStatus(normalized.verificationStatus);
    setDocumentPending(normalized.documentPending);
    setPending(normalized.pending);
    return normalized;
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const response = await getIndividualAccountStatusRequest();
      return applyNormalizedStatus(normalizeStatusResponse(response?.data));
    } catch (error) {
      const local = readLocalIndividualAccount();
      return applyNormalizedStatus(
        normalizeStatusResponse(local || { exists: false, status: "none" }),
      );
    }
  }, [applyNormalizedStatus]);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  const submitOnboarding = useCallback(
    async ({
      personal,
      address,
      company,
      beneficialOwners,
      liveness,
      documents,
      isCompanyAccount,
    }) => {
      setSubmitting(true);

      const payload = isCompanyAccount
        ? buildPjPayload({ company, beneficialOwners, liveness })
        : buildPfPayload({ personal, address, liveness });

      try {
        await createIndividualAccountRequest(payload);
        await submitIndividualAccountKycRequest(documents);
        writeLocalIndividualAccount({ exists: true, status: "pending_review" });
        setStatus("pending_review");
        return true;
      } catch (error) {
        const statusCode = error?.response?.status;

        if (statusCode === 404 || statusCode === 501 || !error?.response) {
          writeLocalIndividualAccount({ exists: true, status: "pending_review", payload });
          setStatus("pending_review");
          return true;
        }

        const message =
          error?.response?.data?.description ||
          error?.response?.data?.information ||
          "Não foi possível enviar sua solicitação. Tente novamente.";

        enqueueSnackbar(message, { variant: "error" });
        return false;
      } finally {
        setSubmitting(false);
      }
    },
    [enqueueSnackbar]
  );

  const canUseIndividualAccount = status === "active";
  const isPendingReview = status === "pending_review";
  const needsOnboarding = status === "none" || status === "rejected" || !status;

  return {
    status,
    verificationStatus,
    documentPending,
    pending,
    submitting,
    isLoadingStatus: status === null,
    canUseIndividualAccount,
    isPendingReview,
    needsOnboarding,
    refreshStatus,
    submitOnboarding,
  };
};
